import React, { useState, useRef } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import StarIcon from '@mui/icons-material/Star';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const SectionContainer = styled(Box)({
  padding: '140px 60px',
  background: '#faf9f7',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '0',
    right: '0',
    width: '600px',
    height: '600px',
    background: 'radial-gradient(circle, rgba(212, 175, 55, 0.08) 0%, transparent 70%)',
    pointerEvents: 'none'
  },
  '@media (max-width: 1024px)': {
    padding: '100px 32px'
  },
  '@media (max-width: 768px)': {
    padding: '80px 20px'
  }
});

const ContentWrapper = styled(Box)({
  maxWidth: '1400px',
  margin: '0 auto',
  position: 'relative',
  zIndex: 10
});

const SectionHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-end',
  marginBottom: '64px',
  animation: `${fadeIn} 0.8s ease-out`,
  '@media (max-width: 900px)': {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '32px'
  }
});

const HeaderLeft = styled(Box)({});

const SectionBadge = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  padding: '10px 24px',
  background: 'rgba(212, 175, 55, 0.1)',
  border: '1px solid rgba(212, 175, 55, 0.2)',
  borderRadius: '50px',
  marginBottom: '24px'
});

const BadgeText = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '12px',
  fontWeight: 600,
  color: '#c9a227',
  textTransform: 'uppercase',
  letterSpacing: '2px'
});

const SectionTitle = styled(Typography)({
  fontFamily: "'Playfair Display', serif",
  fontSize: 'clamp(36px, 5vw, 56px)',
  fontWeight: 400,
  color: '#1a1a1a',
  lineHeight: 1.2,
  letterSpacing: '-1px'
});

const GradientText = styled('span')({
  background: 'linear-gradient(135deg, #c9a227 0%, #e8d5a3 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text'
});

const RatingBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '20px',
  padding: '24px 32px',
  background: '#ffffff',
  borderRadius: '20px',
  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.06)',
  border: '1px solid rgba(0, 0, 0, 0.05)'
});

const RatingNumber = styled(Typography)({
  fontFamily: "'Playfair Display', serif",
  fontSize: '48px',
  fontWeight: 400,
  color: '#1a1a1a',
  letterSpacing: '-2px'
});

const RatingInfo = styled(Box)({});

const StarRow = styled(Box)({
  display: 'flex',
  gap: '4px'
});

const ReviewCount = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '13px',
  fontWeight: 500,
  color: '#888888',
  marginTop: '6px'
});

const ReviewsWrapper = styled(Box)({
  position: 'relative'
});

const ReviewsTrack = styled(Box)({
  display: 'flex',
  gap: '28px',
  overflowX: 'auto',
  scrollBehavior: 'smooth',
  padding: '16px 4px',
  '&::-webkit-scrollbar': {
    display: 'none'
  },
  msOverflowStyle: 'none',
  scrollbarWidth: 'none'
});

const ReviewCard = styled(Box)(({ index }) => ({
  minWidth: '400px',
  maxWidth: '400px',
  background: '#ffffff',
  borderRadius: '24px',
  padding: '40px',
  flexShrink: 0,
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.04)',
  border: '1px solid rgba(0, 0, 0, 0.06)',
  position: 'relative',
  overflow: 'hidden',
  animation: `${fadeIn} 0.8s ease-out`,
  animationDelay: `${index * 0.1}s`,
  animationFillMode: 'backwards',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: 'linear-gradient(90deg, #c9a227, #e8d5a3)',
    opacity: 0,
    transition: 'opacity 0.3s ease'
  },
  '@media (max-width: 768px)': {
    minWidth: '320px',
    maxWidth: '320px',
    padding: '32px'
  },
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1)',
    '&::before': {
      opacity: 1
    }
  }
}));

const QuoteIconWrapper = styled(Box)({
  width: '56px',
  height: '56px',
  borderRadius: '16px',
  background: 'linear-gradient(135deg, rgba(201, 162, 39, 0.1) 0%, rgba(232, 213, 163, 0.1) 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: '24px'
});

const QuoteIcon = styled(FormatQuoteIcon)({
  fontSize: '28px',
  color: '#c9a227',
  transform: 'rotate(180deg)'
});

const ReviewText = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '16px',
  fontWeight: 400,
  color: '#444444',
  lineHeight: 1.9,
  marginBottom: '32px',
  fontStyle: 'italic'
});

const AuthorSection = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  paddingTop: '24px',
  borderTop: '1px solid rgba(0, 0, 0, 0.06)'
});

const Avatar = styled(Box)({
  width: '52px',
  height: '52px',
  borderRadius: '16px',
  overflow: 'hidden',
  background: 'linear-gradient(135deg, #c9a227 0%, #e8d5a3 100%)',
  boxShadow: '0 4px 15px rgba(201, 162, 39, 0.3)'
});

const AvatarImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover'
});

const AvatarPlaceholder = styled(Box)({
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: "'Playfair Display', serif",
  fontSize: '20px',
  fontWeight: 600,
  color: '#ffffff'
});

const AuthorInfo = styled(Box)({
  flex: 1
});

const AuthorName = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '16px',
  fontWeight: 600,
  color: '#1a1a1a',
  marginBottom: '4px'
});

const AuthorRole = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '13px',
  fontWeight: 400,
  color: '#888888'
});

const AuthorRating = styled(Box)({
  display: 'flex',
  gap: '3px'
});

const NavigationWrapper = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  gap: '16px',
  marginTop: '56px'
});

const NavButton = styled(IconButton)({
  width: '56px',
  height: '56px',
  borderRadius: '16px',
  border: '2px solid rgba(0, 0, 0, 0.1)',
  background: '#ffffff',
  color: '#1a1a1a',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'linear-gradient(135deg, #c9a227 0%, #e8d5a3 100%)',
    borderColor: '#c9a227',
    color: '#ffffff',
    boxShadow: '0 8px 30px rgba(201, 162, 39, 0.3)'
  },
  '&:disabled': {
    opacity: 0.3
  }
});

const TestimonialsSection = ({ testimonials }) => {
  const scrollRef = useRef(null);

  // Filter to show only positive reviews (rating >= 4)
  const positiveReviews = testimonials?.filter(t => (t.rating || 5) >= 4) || [];

  if (positiveReviews.length === 0) return null;

  const handleScroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = direction === 'left' ? -428 : 428;
      scrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  const averageRating = (positiveReviews.reduce((acc, t) => acc + (t.rating || 5), 0) / positiveReviews.length).toFixed(1);

  return (
    <SectionContainer id="reviews">
      <ContentWrapper>
        <SectionHeader>
          <HeaderLeft>
            <SectionBadge>
              <BadgeText>Testimonials</BadgeText>
            </SectionBadge>
            <SectionTitle>
              Client <GradientText>Reviews</GradientText>
            </SectionTitle>
          </HeaderLeft>

          <RatingBox>
            <RatingNumber>{averageRating}</RatingNumber>
            <RatingInfo>
              <StarRow>
                {[...Array(5)].map((_, i) => (
                  <StarIcon
                    key={i}
                    sx={{
                      fontSize: 20,
                      color: i < Math.round(parseFloat(averageRating)) ? '#c9a227' : '#e0e0e0'
                    }}
                  />
                ))}
              </StarRow>
              <ReviewCount>{positiveReviews.length} verified reviews</ReviewCount>
            </RatingInfo>
          </RatingBox>
        </SectionHeader>

        <ReviewsWrapper>
          <ReviewsTrack ref={scrollRef}>
            {positiveReviews.map((review, index) => (
              <ReviewCard key={review.id || index} index={index}>
                <QuoteIconWrapper>
                  <QuoteIcon />
                </QuoteIconWrapper>

                <ReviewText>
                  "{review.review || review.comment || review.text}"
                </ReviewText>

                <AuthorSection>
                  <Avatar>
                    {review.image || review.avatar ? (
                      <AvatarImage src={review.image || review.avatar} alt={review.name} />
                    ) : (
                      <AvatarPlaceholder>{(review.name || 'A')[0]}</AvatarPlaceholder>
                    )}
                  </Avatar>

                  <AuthorInfo>
                    <AuthorName>{review.name}</AuthorName>
                    {review.role && <AuthorRole>{review.role}</AuthorRole>}
                  </AuthorInfo>

                  <AuthorRating>
                    {[...Array(5)].map((_, i) => (
                      <StarIcon
                        key={i}
                        sx={{
                          fontSize: 16,
                          color: i < (review.rating || 5) ? '#c9a227' : '#e0e0e0'
                        }}
                      />
                    ))}
                  </AuthorRating>
                </AuthorSection>
              </ReviewCard>
            ))}
          </ReviewsTrack>
        </ReviewsWrapper>

        <NavigationWrapper>
          <NavButton onClick={() => handleScroll('left')}>
            <ChevronLeftIcon />
          </NavButton>
          <NavButton onClick={() => handleScroll('right')}>
            <ChevronRightIcon />
          </NavButton>
        </NavigationWrapper>
      </ContentWrapper>
    </SectionContainer>
  );
};

export default TestimonialsSection;
